import { Link } from '@tanstack/react-router';
import { ChevronsUpDown, LogOut, SunMoon } from 'lucide-react';
import { useThemeState } from '../../stores/ui-store';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '../ui/sidebar';
import type { User, UserMenuGroup } from './types';

/**
 * Meniul de utilizator din footer-ul sidebar-ului.
 *
 * Varianta din shadcn-admin vine cu itemi de demonstraţie („Upgrade to Pro", „Billing")
 * şi îşi face singură delogarea. Aici itemii vin prin `groups`, iar delogarea e ridicată
 * în `AppSidebar`, care ţine dialogul de confirmare — la fel ca în edinro.
 *
 * Comutatorul de temă stă aici, nu în antet: antetul e per pagină, deci un comutator
 * acolo ar dispărea de pe orice ecran care uită să-l pună.
 */

const THEME_OPTIONS = [
  { value: 'system', label: 'Sistem' },
  { value: 'light', label: 'Luminoasă' },
  { value: 'dark', label: 'Întunecată' },
] as const;

type NavUserProps = {
  user: User;
  groups?: UserMenuGroup[];
  signOutLabel?: string;
  openMenuLabel?: string;
  onRequestSignOut: () => void;
};

export function NavUser({
  user,
  groups = [],
  signOutLabel = 'Delogare',
  openMenuLabel = 'Deschide meniul de profil pentru',
  onRequestSignOut,
}: NavUserProps) {
  const { isMobile } = useSidebar();
  const { theme, setTheme } = useThemeState();
  const fallback = user.initials ?? (user.name || user.email).charAt(0).toUpperCase();

  return (
    <SidebarMenu className="p-3">
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              aria-label={`${openMenuLabel} ${user.name}`}
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="size-8 rounded-lg">
                {user.avatar ? <AvatarImage src={user.avatar} alt={user.name} /> : null}
                <AvatarFallback className="rounded-lg">{fallback}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-start text-sm leading-tight">
                <span className="truncate font-semibold">{user.name}</span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
              <ChevronsUpDown className="ms-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-start text-sm">
                <Avatar className="size-8 rounded-lg">
                  {user.avatar ? <AvatarImage src={user.avatar} alt={user.name} /> : null}
                  <AvatarFallback className="rounded-lg">{fallback}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-start text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />

            {groups.map((group) => (
              <div
                key={group.items.map((item) => `${item.label}:${item.href ?? 'action'}`).join('|')}
              >
                <DropdownMenuGroup>
                  {group.items.map((item) =>
                    item.href ? (
                      <DropdownMenuItem key={item.label} asChild>
                        <Link to={item.href as never}>
                          {item.icon ? <item.icon /> : null}
                          {item.label}
                          {item.badge ? (
                            <span className="ms-auto text-xs">{item.badge}</span>
                          ) : null}
                        </Link>
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem key={item.label} onClick={() => item.onSelect?.()}>
                        {item.icon ? <item.icon /> : null}
                        {item.label}
                        {item.badge ? <span className="ms-auto text-xs">{item.badge}</span> : null}
                      </DropdownMenuItem>
                    ),
                  )}
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
              </div>
            ))}

            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <SunMoon />
                Temă
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup value={theme}>
                  {THEME_OPTIONS.map((option) => (
                    <DropdownMenuRadioItem
                      key={option.value}
                      value={option.value}
                      onClick={(event) => {
                        event.preventDefault();
                        setTheme(option.value);
                      }}
                    >
                      {option.label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onClick={onRequestSignOut}>
              <LogOut />
              {signOutLabel}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
